import React from "react"
import {getClosureSizes} from "../../../../domain/manhole";
import {createManholeGeometry} from "./manholeGeometry";
import {
    renderFillLabel,
    renderHorizontalClosureIntersections,
    renderTypePlates,
    renderVerticalClosureIntersections
} from "./renderer";
import {FONT_SIZE} from "../../../../../constants";
import {Arrow, Circle, Group, Rect, Shape, Text} from "react-konva"
import {Line, DashedLine} from "../../../Renderer/primitives";
import {renderSplitLines} from "../../../Renderer/Manhole/d400Vg";

interface ConventionalManholeProps {
    type: string,
    fill: string,
    lidGeometry: any,
    scale: number,
    manhole: any,
    outlineColor: string,
}

export const ConventionalManhole: React.FC<ConventionalManholeProps> =
    ({type, fill, lidGeometry, scale, manhole, outlineColor}) => {
        const manholeGeometry = createManholeGeometry(manhole, scale, lidGeometry);
        const shouldRenderDirections = () => (type === 'D400' || type === 'E600') && manhole.type !== 'VG'
        const renderDirections = () => {
            const rectToVertical = () => {
                return {
                    width: getClosureSizes(manhole.type).width * scale,
                    height: (
                        getClosureSizes(manhole.type).height +
                        2 * (getClosureSizes(manhole.type).rabbetWidth ?? 0)
                    ) * scale
                }
            }
            const rotation = () => manhole.orientation === 'vertical' ? 0 : 180
            const openingDirectionY = () => rectToVertical().height * 0.1
            const drivingDirectionY = () => rectToVertical().height * 0.15
            const labelWidth = () => 110
            const labelLeft = () => (rectToVertical().width - labelWidth()) / 2
            const labelRight = () => labelLeft() + labelWidth()

            const renderArrows = () => {
                const scale = () => {
                    return (manhole.orientation === 'vertical' && manhole.openingDirection === 'right') ||
                    (manhole.orientation === 'horizontal' && manhole.openingDirection === 'up') ? 1 : -1
                }
                const offsetX = () => {
                    return manhole.orientation === 'vertical' ?
                        manhole.openingDirection === 'right' ? 0 : rectToVertical().width :
                        manhole.openingDirection === 'down' ? 0 : rectToVertical().width
                }
                const offsetY = () => {
                    return manhole.orientation === 'vertical' ? 0 : rectToVertical().height
                }
                const renderOpeningDirectionArrow = () => {
                    const arrowLeft = () => labelRight() + 10
                    const arrowRight = () => rectToVertical().width * 0.9

                    return (<Arrow
                        points={[arrowLeft(), openingDirectionY(), arrowRight(), openingDirectionY()]}
                        strokeWidth={1}
                        stroke={'black'}
                        fill={"black"}
                        pointerWidth={5}
                    />)
                }
                const renderDrivingDirectionArrow = () => {
                    const arrowLeft = () => rectToVertical().width * 0.1
                    const arrowRight = () => labelLeft()

                    return (<Arrow
                        points={[arrowRight(), drivingDirectionY(), arrowLeft(), drivingDirectionY()]}
                        strokeWidth={3}
                        stroke={'black'}
                        fill={"black"}
                        pointerWidth={7}
                    />)
                }

                return (<React.Fragment>
                    <Group scaleX={scale()} rotation={rotation()} offsetX={offsetX()} offsetY={offsetY()} name={'fill'}>
                        {renderOpeningDirectionArrow()}
                        {renderDrivingDirectionArrow()}
                    </Group>
                </React.Fragment>)
            }
            const renderLabels = () => {
                const offsetX = () => {
                    return manhole.orientation === 'vertical' ? 0 : rectToVertical().width
                }
                const offsetY = () => {
                    return manhole.orientation === 'vertical' ? 0 : rectToVertical().height
                }
                const renderOpeningDirectionLabel = () => {
                    const labelTop = () => openingDirectionY() - FONT_SIZE / 2

                    return (<Text
                        name="fill"
                        x={labelLeft()}
                        y={labelTop()}
                        width={labelWidth()}
                        height={FONT_SIZE}
                        fontSize={FONT_SIZE}
                        align="center"
                        verticalAlign="bottom"
                        text={"Öffnungsrichtung"}
                        fill="black"
                    />)
                }
                const renderDrivingDirectionLabel = () => {
                    const labelTop = () => drivingDirectionY() - FONT_SIZE / 2

                    return (<Text
                        name="fill"
                        x={labelLeft()}
                        y={labelTop()}
                        width={labelWidth()}
                        height={FONT_SIZE}
                        fontSize={FONT_SIZE}
                        align="center"
                        verticalAlign="bottom"
                        text={"Fahrtrichtung"}
                        fill="black"
                    />)
                }
                return (<Group rotation={rotation()} offsetX={offsetX()} offsetY={offsetY()} name={'fill'}>
                    {renderOpeningDirectionLabel()}
                    {renderDrivingDirectionLabel()}
                </Group>)
            }

            return (<React.Fragment>
                {renderArrows()}
                {renderLabels()}
            </React.Fragment>)
        }

        const renderManholeOutline = () =>
            <Line
                points={[
                    manholeGeometry.left,
                    manholeGeometry.top,
                    manholeGeometry.left,
                    manholeGeometry.bottom,
                    manholeGeometry.right,
                    manholeGeometry.bottom,
                    manholeGeometry.right,
                    manholeGeometry.top,
                    manholeGeometry.left,
                    manholeGeometry.top
                ]}
                stroke={outlineColor}
            />
        const getGirderWidth = () => 18 * scale
        const renderRabbetLines = () => {
            return (<React.Fragment>
                <DashedLine points={[
                    0,
                    manholeGeometry.clearTop,
                    manholeGeometry.width,
                    manholeGeometry.clearTop
                ]}/>
                <DashedLine points={[
                    0,
                    manholeGeometry.clearBottom,
                    manholeGeometry.width,
                    manholeGeometry.clearBottom
                ]}/>
            </React.Fragment>)
        }
        const renderA15Keyholes = () => {
            const getHorizontalKeyholeSpacing = () => 12.5 * scale
            const getVerticalKeyholeSpacing = () => 15 * scale

            const renderKeyhole = (x: number, y: number) => {
                const getLength = () => 3 * scale
                const getWidth = () => 1.5 * scale
                const getRightCenterX = () => (getLength() - getWidth()) / 2
                const getLeftCenterX = () => -getRightCenterX()
                const getRadius = () => getWidth() / 2

                return (<Group x={x} y={y}>
                    <Shape
                        sceneFunc={(context, shape) => {
                            context.beginPath()
                            context.arc(getLeftCenterX(), 0, getRadius(), Math.PI / 2, -Math.PI / 2, false)
                            context.arc(getRightCenterX(), 0, getRadius(), -Math.PI / 2, Math.PI / 2, false)
                            context.closePath()
                            context.fillStrokeShape(shape)
                        }}
                        stroke="black"
                        strokeWidth={1}
                    />
                </Group>)
            }

            return ([

                manholeGeometry.left,
                ...manholeGeometry.verticalClosureIntersectionsX
            ].map(x => [
                manholeGeometry.top,
                ...manholeGeometry.horizontalClosureIntersectionsY
            ].map(y => <Group x={x} y={y} key={'keyhole_' + x + '_' + y}>
                {renderKeyhole(getHorizontalKeyholeSpacing(), getVerticalKeyholeSpacing())}
                {renderKeyhole(getHorizontalKeyholeSpacing(), manholeGeometry.closureHeight -
                    getVerticalKeyholeSpacing())}
            </Group>)))
        }
        const renderB125Girder = () => {
            return manholeGeometry.horizontalClosureIntersectionsY
                .map((p, i) => (<React.Fragment key={'girder_' + i}>
                    <DashedLine points={[manholeGeometry.left,
                        p - getGirderWidth() / 2,
                        manholeGeometry.right,
                        p - getGirderWidth() / 2]}/>
                    <DashedLine points={[
                        manholeGeometry.left,
                        p + getGirderWidth() / 2,
                        manholeGeometry.right,
                        p + getGirderWidth() / 2]}/>
                </React.Fragment>))
        }
        const renderB125Keyholes = () => {
            const getHorizontalKeyholeSpacing = () => 5 * scale
            const getVerticalKeyholeSpacing = () => 6 * scale

            const renderKeyhole = (x: number, y: number) => {
                const getKeyholeRadius = () => 2 * scale
                const getLineYDistance = () => 0.5 * scale
                const getLineXDistance = () => Math.sqrt(Math.pow(getKeyholeRadius(), 2) -
                    Math.pow(getLineYDistance(), 2))

                return (<Group x={x} y={y}>
                    <Circle radius={getKeyholeRadius()} stroke={'black'} strokeWidth={1}/>
                    <Line points={[-getLineXDistance(),
                        -getLineYDistance(),
                        getLineXDistance(),
                        -getLineYDistance()]}/>
                    <Line
                        points={[-getLineXDistance(), getLineYDistance(), getLineXDistance(), getLineYDistance()]}/>
                </Group>)
            }

            return ([
                    manholeGeometry.left,
                    ...manholeGeometry.verticalClosureIntersectionsX
                ].map(x => [
                        manholeGeometry.top,
                        ...manholeGeometry.horizontalClosureIntersectionsY
                    ].map(y => <Group x={x} y={y} key={'keyhole_' + x + '_' + y}>
                            {renderKeyhole(getHorizontalKeyholeSpacing(), getVerticalKeyholeSpacing())}
                            {renderKeyhole(manholeGeometry.closureWidth -
                                getHorizontalKeyholeSpacing(), getVerticalKeyholeSpacing())}
                            {renderKeyhole(getHorizontalKeyholeSpacing(), manholeGeometry.closureHeight -
                                getVerticalKeyholeSpacing())}
                            {renderKeyhole(manholeGeometry.closureWidth -
                                getHorizontalKeyholeSpacing(), manholeGeometry.closureHeight -
                                getVerticalKeyholeSpacing())}
                        </Group>
                    )
                )
            )
        }
        const renderB125ScrewHoles = () => {
            const renderScrewHole = (x: number, y: number) => {
                const getRadius = () => 1.5 * scale
                const getInnerRadius = () => scale

                return (<Group x={x} y={y}>
                    <Circle radius={getRadius()} stroke={'black'} strokeWidth={1}/>
                    <Circle radius={getInnerRadius()} stroke={'black'} strokeWidth={1}/>
                </Group>)
            }

            const renderRabbetScrewHoles = () => {
                const getYDistance = () => 1.75 * scale
                const getCenter = () => manholeGeometry.closureWidth / 2

                return ([manholeGeometry.left,
                    ...manholeGeometry.verticalClosureIntersectionsX].map((x, i) => (
                    <React.Fragment key={'rabbet_screw_hole_' + i}>
                        {renderScrewHole(x + getCenter(), manholeGeometry.top +
                            getYDistance())}
                        {renderScrewHole(x + getCenter(), manholeGeometry.bottom -
                            getYDistance())}
                    </React.Fragment>)))
            }

            const renderGirderScrewHoles = () => {
                const getYDistance = () => 4.5 * scale
                const getXCenter = () => manholeGeometry.closureWidth / 2

                return ([
                    manholeGeometry.left,
                    ...manholeGeometry.verticalClosureIntersectionsX
                ].map(x => manholeGeometry.horizontalClosureIntersectionsY
                    .map(y => (<React.Fragment key={'girder_screw_hole_' + x + '_' + y}>
                        {renderScrewHole(x + getXCenter(), y + getYDistance())}
                        {renderScrewHole(x + getXCenter(), y - getYDistance())}
                    </React.Fragment>))))
            }

            return (<React.Fragment>
                {renderRabbetScrewHoles()}
                {renderGirderScrewHoles()}
            </React.Fragment>)
        }
        const shouldRenderD400E600Keyholes = () => manhole.type !== 'VG'
        const renderD400E600Keyholes = () => {
            const getKeyholeWidth = () => 10 * scale
            const getKeyholeHeight = () => 5 * scale

            const renderKeyhole = (x: number, y: number) => {
                const getInnerRectWidth = () => 3 * scale
                const getInnerRectHeight = () => 1.5 * scale
                const getInnerRectX = () => (getKeyholeWidth() - getInnerRectWidth()) / 2
                const getInnerRectY = () => (getKeyholeHeight() - getInnerRectHeight()) / 2

                return (<Group x={x} y={y}>
                    <Rect width={getKeyholeWidth()} height={getKeyholeHeight()} stroke={'black'}
                          strokeWidth={1}/>
                    <Rect width={getInnerRectWidth()} height={getInnerRectHeight()} stroke={'black'}
                          strokeWidth={1}
                          x={getInnerRectX()} y={getInnerRectY()}/>
                </Group>)
            }

            return (
                [
                    manholeGeometry.left,
                    ...manholeGeometry.verticalClosureIntersectionsX
                ].map(x => [
                        manholeGeometry.top,
                        ...manholeGeometry.horizontalClosureIntersectionsY
                    ].map(y =>
                        <Group x={x} y={y} key={'keyhole_' + x + '_' + y}>
                            {renderKeyhole(0, 0)}
                            {renderKeyhole(manholeGeometry.closureWidth - getKeyholeWidth(), 0)}
                            {renderKeyhole(0, manholeGeometry.closureHeight - getKeyholeHeight())}
                            {renderKeyhole(manholeGeometry.closureWidth -
                                getKeyholeWidth(), manholeGeometry.closureHeight - getKeyholeHeight())}
                        </Group>
                    )
                )
            )
        }
        const shouldRenderFillLabels = () => manhole.type !== 'VG'
        const renderFillLabels = () => {
            return [
                manholeGeometry.left,
                ...manholeGeometry.verticalClosureIntersectionsX
            ].map(x => [
                    manholeGeometry.top,
                    ...manholeGeometry.horizontalClosureIntersectionsY
                ].map(y =>
                    <Group x={x + 19 * scale} y={y + manholeGeometry.closureHeight / 2}
                           key={'fill_label_' + x + '_' + y}>
                        {renderFillLabel(fill)}
                    </Group>
                )
            )
        }

        const shouldRenderVgSplitLine = () => manhole.type === 'VG'
        const renderVgSplitLine = () => {
            return renderSplitLines(manholeGeometry)
        }

        return (<Group x={manholeGeometry.tx} y={manholeGeometry.ty} rotation={manholeGeometry.rot}>
            {renderManholeOutline()}
            {renderRabbetLines()}
            {renderHorizontalClosureIntersections(manholeGeometry)}
            {renderVerticalClosureIntersections(manholeGeometry)}
            {type === 'A15' && renderA15Keyholes()}
            {type === 'B125' && renderB125Girder()}
            {type === 'B125' && renderB125Keyholes()}
            {type === 'B125' && renderB125ScrewHoles()}
            {(type === 'D400' || type === 'E600') && shouldRenderD400E600Keyholes() && renderD400E600Keyholes()}
            {shouldRenderDirections() && renderDirections()}
            {shouldRenderDirections() && renderDirections()}
            {renderTypePlates(manholeGeometry, scale, manhole)}
            {shouldRenderFillLabels() && renderFillLabels()}
            {shouldRenderVgSplitLine() && renderVgSplitLine()}
        </Group>)
    }